import { useLayoutEffect, useRef, useState } from "react";
import type { AnnieChatItem } from "./useAnnie";

export interface UseAnnieUnreadReplyInput {
	isOpen: boolean;
	isStreaming: boolean;
	/** `useAnnie().isLoadingSession`: a past conversation's transcript is loading. */
	isLoadingSession: boolean;
	items: AnnieChatItem[];
}

/** The answer the running turn is still streaming in: an assistant item after the latest question. */
const answerInFlightId = (items: AnnieChatItem[], isStreaming: boolean): string | undefined => {
	if (!isStreaming) return undefined;
	const latestQuestion = items.map((item) => item.kind).lastIndexOf("user");
	return items
		.slice(latestQuestion + 1)
		.filter((item) => item.kind === "assistant")
		.at(-1)?.id;
};

/** Approval results reuse the card's ID, but each outcome still needs to be read. */
const unreadItemKey = (item: AnnieChatItem): string => {
	if (item.kind === "proposal" && (item.status === "done" || item.status === "stale" || item.status === "expired" || item.status === "failed")) {
		return `${item.id}:${item.status}`;
	}
	return item.id;
};

/**
 * How many answers, cards and errors landed without the user seeing them finished with the panel open — a turn's
 * reply, or the result of a choice or approval made just before closing. Tracked by item and approval outcome,
 * so a turn that rolls back its question bubble or restores an earlier answer never re-counts what was already read. A past conversation's
 * transcript is history, not replies, even when it finishes loading after the panel closed; an emptied transcript
 * (a department switch) counts nothing.
 */
export const useAnnieUnreadReply = ({ isOpen, isStreaming, isLoadingSession, items }: UseAnnieUnreadReplyInput): number => {
	const [seenItemKeys, setSeenItemKeys] = useState<ReadonlySet<string>>(() => new Set());
	const wasLoadingSessionRef = useRef(isLoadingSession);
	const previousItemIdsRef = useRef<ReadonlySet<string>>(new Set());
	const inFlightId = answerInFlightId(items, isStreaming);

	// Layout, not passive: a transcript that loads while closed is marked before its first paint, so it never flashes a count.
	useLayoutEffect(() => {
		// The loaded transcript lands in the same render that ends the load.
		const finishedLoading = wasLoadingSessionRef.current && !isLoadingSession;
		wasLoadingSessionRef.current = isLoadingSession;
		const previousItemIds = previousItemIdsRef.current;
		previousItemIdsRef.current = new Set(items.map((item) => item.id));
		const loadedHistory = finishedLoading ? items.filter((item) => item.kind !== "error" && !previousItemIds.has(item.id)) : [];
		const seenNow = isOpen ? items.filter((item) => item.id !== inFlightId) : loadedHistory;
		const newlySeen = seenNow.map(unreadItemKey).filter((key) => !seenItemKeys.has(key));
		if (newlySeen.length > 0) setSeenItemKeys(new Set([...seenItemKeys, ...newlySeen]));
	}, [isOpen, isLoadingSession, items, inFlightId, seenItemKeys]);

	if (isOpen) return 0;
	return items.filter((item) => item.kind !== "user" && item.id !== inFlightId && !seenItemKeys.has(unreadItemKey(item))).length;
};
